import { registerLocaleData } from '@angular/common';
import { Component, DestroyRef, DOCUMENT, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { TranslateService } from '@ngx-translate/core';
import {
    ConstantsService,
    CustomIconsService,
    langCodeToIntlFEData,
    LocaleService,
    LocalizationService,
    SignalRService,
} from 'lemunzo-shared';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { filter, startWith, take } from 'rxjs';
import { environment } from '../environments/environment';
import { ErrorToastGenericComponent } from './core/components/error-toast-generic/error-toast-generic.component';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    imports: [
        ConfirmDialogModule,
        ErrorToastGenericComponent,
        RouterOutlet,
        ToastModule,
    ],
})
export class AppComponent implements OnInit {
    readonly TOAST_KEY = ConstantsService.TOAST_KEY;
    readonly CAN_DEACTIVATE_CONFIRM_KEY =
        ConstantsService.CAN_DEACTIVATE_CONFIRM_KEY;

    constructor(
        private readonly authService: MsalService,
        private readonly customIconsService: CustomIconsService,
        private readonly destroyRef: DestroyRef,
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly localeService: LocaleService,
        private readonly localizationService: LocalizationService,
        private readonly msalBroadcastService: MsalBroadcastService,
        private readonly signalR: SignalRService,
        private readonly translateService: TranslateService
    ) {}

    ngOnInit(): void {
        // Subscribing to handleRedirectObservable before any other functions both initializes the application and ensures redirects are handled
        this.authService
            .handleRedirectObservable({ navigateToLoginRequestUrl: false })
            .subscribe();

        // configures the document's language once and then updates the value upon every language change by the user
        this.translateService.onLangChange
            .pipe(
                startWith({
                    lang: this.localizationService.getCurrentLanguage(),
                    translations: null,
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: ({ lang }) => {
                    this.document.documentElement.lang = lang;
                },
            });

        this.localeService.handleApplicationLanguages();

        // Register locales
        langCodeToIntlFEData.forEach((data, langCode) => {
            registerLocaleData(
                data.angularLocale,
                langCode,
                data.angularLocaleExtra
            );
        });

        // Initialize custom icons
        this.customIconsService.initializeCustomIcons();

        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status) => status === InteractionStatus.None),
                take(1),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(() => {
                this.signalR.start(
                    environment.apiUrl,
                    environment.authConfig.apiConfig.scopes
                );
            });
    }
}
